import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, AlertTriangle, Info, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { useWizard } from "@/context/WizardContext";
import { SectionHeading } from "@/components/wizard/FormKit";
import { fieldToStep } from "@/lib/fieldToStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY = {
  error: { icon: AlertTriangle, color: "text-[#c05746]", bg: "bg-[#fbeae5] border-[#e2b6a9]" },
  warning: { icon: AlertTriangle, color: "text-[#a07a27]", bg: "bg-[#fbf2d8] border-[#DBB90A]" },
  info: { icon: Info, color: "text-[#3a5b4d]", bg: "bg-[#E7F1FB] border-[#B7CADA]" },
};

export default function Step8AIReview() {
  const { data, setData } = useWizard();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const review = data.ai_review;

  const trigger = async () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      toast.error("You're offline — reconnect to run AI review. Your draft is safe.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/wizard/ai-review`, data, { timeout: 60000 });
      setData((d) => ({ ...d, ai_review: res.data }));
      toast.success("AI clinical review complete.");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`AI review failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const jumpToIssue = (issue) => {
    const target = fieldToStep(issue.field);
    if (!target) {
      toast.info("This issue isn't tied to a specific step.");
      return;
    }
    // Stash which field to focus so the destination step can scroll/ring it.
    // Cleared automatically by stepValidation.focusMissing on arrival.
    try {
      sessionStorage.setItem(
        "wizard_focus_field",
        JSON.stringify({ field: target.fieldPath, step: target.step, ts: Date.now() })
      );
    } catch { /* sessionStorage unavailable — non-fatal */ }
    navigate(`/wizard/${target.step}`);
  };

  return (
    <div className="space-y-8">
      <section className="card-surface fade-up">
        <SectionHeading
          eyebrow="Step 8 · Triple Verification (1 of 3)"
          title="AI Clinical Review"
          description="Claude Sonnet 4.5 scans the full payload for missing fields, inconsistencies, and Wisconsin PA compliance."
        />
        <button type="button" onClick={trigger} disabled={loading} className="btn-primary" data-testid="ai-review-trigger">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Running clinical review…</> : <><Sparkles className="w-4 h-4" /> Trigger AI Clinical Review</>}
        </button>
      </section>

      {review && (
        <section className="ai-panel fade-up" data-testid="ai-review-results">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-[#0A3D79]" />
              <h3 className="font-display text-xl font-semibold text-[#0B1E3A]">AI Review Result</h3>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275]">Completeness</div>
                <div className="text-2xl font-display font-bold text-[#0A3D79]" data-testid="ai-score">
                  {review.completeness_score ?? "—"}<span className="text-[13px] text-[#3A5275] font-normal">/100</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275]">Ready to Submit</div>
                <div className={`text-[15px] font-semibold flex items-center gap-1 ${review.ready_to_submit ? "text-[#0A3D79]" : "text-[#c05746]"}`} data-testid="ai-ready">
                  {review.ready_to_submit ? <><CheckCircle2 className="w-4 h-4" /> Yes</> : <><AlertTriangle className="w-4 h-4" /> Not yet</>}
                </div>
              </div>
            </div>
          </div>

          {review.summary && (
            <div className="bg-white/70 rounded-lg p-4 border border-[#B7CADA] mb-4 text-[14.5px] leading-relaxed text-[#0B1E3A]" data-testid="ai-summary">
              {review.summary}
            </div>
          )}

          <div className="space-y-2">
            {(review.issues || []).map((issue, idx) => {
              const s = SEVERITY[issue.severity] || SEVERITY.info;
              const Icon = s.icon;
              const target = fieldToStep(issue.field);
              const isFixable = !!target && (issue.severity === "error" || issue.severity === "warning");
              const key = `${issue.rule || "ai"}-${issue.field || "x"}-${idx}`;
              return (
                <div key={key} className={`${s.bg} border rounded-lg px-4 py-3 flex items-start gap-3`} data-testid={`ai-issue-${idx}`}>
                  <Icon className={`${s.color} w-5 h-5 mt-0.5 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] uppercase tracking-[0.14em] font-semibold ${s.color}`}>{issue.severity}</span>
                      {issue.rule && (
                        <span
                          className="text-[10.5px] font-mono uppercase tracking-[0.08em] bg-[#0f3b5f] text-white px-1.5 py-0.5 rounded"
                          data-testid={`ai-issue-${idx}-rule`}
                        >
                          {issue.rule}
                        </span>
                      )}
                      <span className="text-[12px] font-mono text-[#1E2E4A]">{issue.field || "—"}</span>
                    </div>
                    <div className="text-[14px] text-[#0B1E3A] mt-0.5">{issue.message}</div>
                  </div>
                  {isFixable && (
                    <button
                      type="button"
                      onClick={() => jumpToIssue(issue)}
                      className="self-center shrink-0 inline-flex items-center gap-1.5 rounded-md border border-[#0A3D79] bg-white hover:bg-[#E7F1FB] text-[#0A3D79] text-[12.5px] font-semibold px-3 py-1.5 transition"
                      data-testid={`ai-issue-${idx}-fix`}
                      title={`Go to Step ${target.step} · ${target.label}`}
                    >
                      Fix in Step {target.step}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
            {(review.issues || []).length === 0 && (
              <div className="text-center py-6 text-[#3A5275]">No issues detected.</div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
